import React, { useState, useRef, useEffect } from 'react';
import { getLocalizedValue } from '../lib/cms';
import { useLanguage } from '../contexts/LanguageContext';

export default function TestimonialCard({ item }) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const textRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        // Check if the content's scroll height is greater than its visible height (h-52 is 208px)
        setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };

    // Run check immediately
    checkOverflow();

    // Re-check on window resize to handle responsive layout changes
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [item.text]);

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <>
      <div className="bg-white rounded-none border border-bluegray p-8 shadow-xl flex flex-col justify-between relative hover:shadow-2xl transition-shadow duration-300 overflow-hidden h-full">
        {/* Quote Icon as background watermark */}
        <span className="absolute top-2 right-6 text-8xl font-serif text-navy-100/50 pointer-events-none select-none z-0">”</span>
        
        <div className="mb-6 relative z-10 flex-1 flex flex-col justify-between">
          <div className="relative">
            {/* Testimonial Text Container with fixed height */}
            <div 
              ref={textRef}
              className="text-sm text-charcoal/85 leading-relaxed space-y-3 font-sans italic h-52 overflow-hidden relative
                         [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3 [&_ul]:space-y-1.5 [&_li]:text-charcoal/80 [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: getLocalizedValue(item.text, language) }}
            />
            
            {/* Fade-out gradient and "Læs hele" button if overflowing */}
            {isOverflowing && (
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/95 to-transparent flex items-end justify-center pb-1">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="text-xs font-bold text-petrol hover:text-petrol-dark uppercase tracking-wider bg-white px-4 py-2 border border-bluegray shadow-sm hover:shadow transition-all duration-200 cursor-pointer flex items-center gap-1.5"
                >
                  <i className="fa-light fa-magnifying-glass-plus"></i> {language === 'da' ? 'Læs hele udtalelsen' : language === 'en' ? 'Read full testimonial' : 'Ganze Bewertung lesen'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Client Info */}
        <div className="border-t border-bluegray/60 pt-6 mt-auto relative z-10">
          <h4 className="font-serif text-base font-bold text-navy-900">{item.client_name}</h4>
          <p className="text-xs text-petrol font-bold uppercase tracking-wider mt-1">{item.company}</p>
          {item.context && (
            <p className="text-[11px] text-navy-900/60 mt-2 leading-relaxed border-l-2 border-petrol/30 pl-2.5">
              {getLocalizedValue(item.context, language)}
            </p>
          )}
        </div>
      </div>

      {/* Testimonial Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/80 p-4 backdrop-blur-sm transition-opacity duration-300"
          onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}
        >
          <div className="bg-white rounded-none border border-bluegray max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 sm:p-10 shadow-2xl relative flex flex-col">
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-navy-900/60 hover:text-navy-900 text-2xl transition-colors p-2 focus:outline-none cursor-pointer"
              aria-label="Luk udtalelse"
            >
              <i className="fa-light fa-xmark text-2xl"></i>
            </button>

            {/* Quote Watermark in Modal */}
            <span className="absolute top-4 right-10 text-9xl font-serif text-navy-100/30 pointer-events-none select-none z-0">”</span>

            {/* Modal Content */}
            <div className="relative z-10 flex-1">
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold block mb-4">// KUNDEUDFTALELSE</span>
              
              <div 
                className="text-base text-charcoal/90 leading-relaxed space-y-4 font-sans italic mb-8 pr-2
                           [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ul]:space-y-2 [&_li]:text-charcoal/80 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: getLocalizedValue(item.text, language) }}
              />

              {/* Client Info at bottom of modal */}
              <div className="border-t border-bluegray/60 pt-6 mt-6">
                <h4 className="font-serif text-lg font-bold text-navy-900">{item.client_name}</h4>
                <p className="text-xs text-petrol font-bold uppercase tracking-wider mt-1">{item.company}</p>
                {item.context && (
                  <p className="text-xs text-navy-900/60 mt-2 leading-relaxed border-l-2 border-petrol/30 pl-3">
                    {getLocalizedValue(item.context, language)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
